import { useEffect, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AudienceNavbar } from './AudienceNavbar';
import { AudienceFooter } from './AudienceFooter';
import { boardsStore, generateBoardId } from '../../stores/boardsStore';
import { getEmployeesArray } from '../../employees/employeesStore';
import { useSeoMeta } from '../../hooks/useSeoMeta';

const shared = {
  de: { ctaBoard: 'Board erstellen', ctaCanvas: 'Canvas erstellen' },
  en: { ctaBoard: 'Create Board', ctaCanvas: 'Create Canvas' },
} as const;

export type BentoItem = {
  span: 1 | 2;
  label: string;
  title: string;
  body: string;
  variant?: 'default' | 'accent' | 'muted';
};

export type AudienceContent = {
  title: string;
  metaDesc: string;
  heroImg?: { src: string; pos?: string };
  hero: {
    h1: string;
    lead: string;
  };
  bento: {
    heading: string;
    items: readonly BentoItem[];
  };
  narrative: {
    heading: string;
    paragraphs: readonly string[];
  };
  cta: {
    heading: string;
    body: string;
  };
};

export function AudiencePageLayout({
  lang,
  slug,
  content,
  basePath = 'for',
}: {
  lang: 'de' | 'en';
  slug: string;
  content: AudienceContent;
  basePath?: string;
}) {
  const navigate = useNavigate();
  const s = shared[lang];

  const otherLang = lang === 'de' ? 'en' : 'de';
  const path = `/${lang}/${basePath}/${slug}`;
  const alternatePath = `/${otherLang}/${basePath}/${slug}`;

  useSeoMeta({ title: content.title, description: content.metaDesc, lang, path, alternatePath });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug, lang]);

  const goToBoards = useCallback(() => { window.scrollTo(0, 0); navigate({ to: '/boards' }); }, [navigate]);
  const goToCanvas = useCallback(() => {
    const now = new Date().toISOString();
    const today = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;
    const boardId = generateBoardId('canvas');
    boardsStore.send({ type: 'board.create', board: { id: boardId, name: 'Canvas', startDate: today, createdAt: now, updatedAt: now, employeeIds: getEmployeesArray().map(e => e.id) } });
    window.scrollTo(0, 0);
    navigate({ to: '/boards/$boardId/canvas', params: { boardId } });
  }, [navigate]);

  return (
    <div className="landing-root">
      <AudienceNavbar lang={lang} slug={slug} basePath={basePath} />

      <article>
        <section className="audience-hero">
          <h1>{content.hero.h1}</h1>
          <p className="audience-hero-lead">{content.hero.lead}</p>
          <div className="audience-hero-actions">
            <button className="landing-btn-primary" onClick={goToBoards}>{s.ctaBoard}</button>
            <button className="landing-btn-primary" onClick={goToCanvas}>{s.ctaCanvas}</button>
          </div>
        </section>

        {content.heroImg && (
          <div className="audience-hero-img">
            <img src={content.heroImg.src} alt="" style={content.heroImg.pos ? { objectPosition: content.heroImg.pos } : undefined} />
          </div>
        )}

        <section className="audience-bento-section">
          <h2 className="audience-bento-heading">{content.bento.heading}</h2>
          <div className="audience-bento">
            {content.bento.items.map((item, i) => (
              <div
                key={i}
                className={[
                  'audience-bento-item',
                  item.span === 2 ? 'audience-bento-item--span-2' : '',
                  item.variant === 'accent' ? 'audience-bento-item--accent' : '',
                  item.variant === 'muted' ? 'audience-bento-item--muted' : '',
                ].filter(Boolean).join(' ')}
              >
                <span className="audience-bento-label">{item.label}</span>
                <h3 className="audience-bento-title">{item.title}</h3>
                <p className="audience-bento-body">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="audience-narrative">
          <h2>{content.narrative.heading}</h2>
          {content.narrative.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </section>

        <section className="audience-cta-section">
          <h2>{content.cta.heading}</h2>
          <p>{content.cta.body}</p>
          <div className="audience-cta-actions">
            <button className="landing-btn-primary" onClick={goToBoards}>{s.ctaBoard}</button>
            <button className="landing-btn-primary" onClick={goToCanvas}>{s.ctaCanvas}</button>
          </div>
        </section>
      </article>

      <AudienceFooter lang={lang} currentSlug={slug} basePath={basePath} />
    </div>
  );
}
